import React from "react";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { Button, Grid2 } from "@mui/material";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { AccountHolder } from "@rentablo/api/apiv1";
import { RHFTextField } from "@rentablo/components";
import { useUpdateReferenceAccount } from "@rentablo/api/apiv1";

const Form = styled.form`
  display: flex;
  padding: ${({ theme }) => theme.spacing(4, 0)};
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  max-width: 700px;
`;

const FormActions = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  padding-top: ${({ theme }) => theme.spacing(6)};

  ${({ theme }) => theme.breakpoints.down("md")} {
    padding: ${({ theme }) => theme.spacing(2, 0)};
    flex-direction: column;
    align-items: unset;
  }
`;

interface FormFields {
  iban: string;
  bankName: string;
  bic: string;
  bankLocation: string;
  accountHolderFirstName: string;
  accountHolderLastName: string;
}

export interface PaymentDataFormProps {
  accountHolder: AccountHolder;
}

export const PaymentDataForm = (props: PaymentDataFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { accountHolder } = props;

  const { referenceAccount: account } = accountHolder;

  const schema = Yup.object().shape({
    iban: Yup.string().required(t("form.errors.required")),
    bankName: Yup.string().required(t("form.errors.required")),
    bic: Yup.string().required(t("form.errors.required")),
    bankLocation: Yup.string().required(t("form.errors.required")),
    accountHolderFirstName: Yup.string().required(t("form.errors.required")),
    accountHolderLastName: Yup.string().required(t("form.errors.required")),
  });

  const { handleSubmit, control, setError } = useForm<FormFields>({
    resolver: yupResolver(schema),
    defaultValues: {
      iban: account?.iban,
      bankName: account?.bankName,
      bic: account?.bic,
      bankLocation: account?.bankLocation,
      accountHolderFirstName: account?.accountHolderFirstName,
      accountHolderLastName: account?.accountHolderLastName,
    },
  });

  const { mutate } = useUpdateReferenceAccount({
    onSuccess: () => navigate("/cashback?tab=contacts"),
    throwOnError: false,
    onError: (error) => {
      setError("iban", {
        message:
          error.response?.data.message ===
          "IBAN konnte nicht gespeichert werden"
            ? t("domains.cashback.paymentDataForm.ibanError")
            : error.response?.data.message,
      });
    },
  });

  const onSubmit = (form: FormFields) => {
    mutate({
      id: accountHolder.id,
      createOrUpdateBankAccountParams: form,
    });
  };

  return (
    <Form
      onSubmit={handleSubmit(onSubmit)}
      data-testid="cashback-dashboard-edit-contact"
    >
      <Grid2 container columnSpacing={3} rowSpacing={6} mb={6}>
        <Grid2 size={12}>
          <RHFTextField
            label={t("domains.cashback.paymentDataForm.iban")}
            name="iban"
            control={control}
            fullWidth
          />
        </Grid2>
        <Grid2 size={12}>
          <RHFTextField
            label={t("domains.cashback.paymentDataForm.bank")}
            name="bankName"
            control={control}
            fullWidth
          />
        </Grid2>
      </Grid2>
      <Grid2 container columnSpacing={3} rowSpacing={6}>
        <Grid2
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <RHFTextField
            label={t("domains.cashback.paymentDataForm.bic")}
            name="bic"
            control={control}
            fullWidth
          />
        </Grid2>
        <Grid2
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <RHFTextField
            label={t("domains.cashback.paymentDataForm.bankLocation")}
            name="bankLocation"
            control={control}
            fullWidth
          />
        </Grid2>
        <Grid2
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <RHFTextField
            label={t("domains.cashback.paymentDataForm.firstName")}
            name="accountHolderFirstName"
            control={control}
            fullWidth
          />
        </Grid2>
        <Grid2
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <RHFTextField
            label={t("domains.cashback.paymentDataForm.lastName")}
            name="accountHolderLastName"
            control={control}
            fullWidth
          />
        </Grid2>
      </Grid2>
      <FormActions>
        <Button type="submit" variant="primary">
          {t("domains.cashback.form.save")}
        </Button>
        <Button
          variant="tertiary"
          onClick={() => navigate("/cashback?tab=contacts")}
        >
          {t("domains.cashback.form.cancel")}
        </Button>
      </FormActions>
    </Form>
  );
};
